// 11 §11.1 SessionStore. Phases: booting (wasm + atlas) → auth (sign-in) → connecting (SDK) →
// ready | incompatible (11 §11.4) | offline (the first connect failed; retried, docs/54 RC6). A drop
// after that is the LinkStore's (core/stores/link.ts), not a phase. The world the
// human last chose is remembered per database so a reload lands back in it.
import { create } from 'zustand';
import { serverNow, serverOffsetMs } from '../time';

export type SessionPhase = 'booting' | 'auth' | 'connecting' | 'ready' | 'incompatible' | 'offline';

export interface Compat { rulesetVersion: number; minClient: string; moduleVersion?: string }

export interface SessionStore {
  phase: SessionPhase;
  identity?: string; token?: string;              // identity hex; the token is also in localStorage
  worldId?: bigint; serverOffsetMs: number;
  compat?: Compat;
  wasmRuleset?: number;
  error?: string;
  signIn(): Promise<void>; signOut(): void; serverNow(): number;
  // shell-internal
  setPhase(phase: SessionPhase): void;
  connected(identity: string, token: string): void;
  setCompat(row: Compat | undefined, wasmRuleset: number, clientVersion: string): void;
  offline(error: string): void;
  retry(): void;
  setWorld(worldId: bigint | undefined): void;
  touchOffset(): void;
}

const WORLD_KEY = `fk.world.${import.meta.env.VITE_STDB_DB ?? 'fellowkeep-local'}`;

function loadWorld(): bigint | undefined {
  try { const v = localStorage.getItem(WORLD_KEY); return v ? BigInt(v) : undefined; } catch { return undefined; }
}

/** semver `a >= b` on the first three components. */
export function semverAtLeast(a: string, b: string): boolean {
  const pa = a.split('.').map((n) => parseInt(n, 10) || 0), pb = b.split('.').map((n) => parseInt(n, 10) || 0);
  for (let i = 0; i < 3; i++) { if ((pa[i] ?? 0) > (pb[i] ?? 0)) return true; if ((pa[i] ?? 0) < (pb[i] ?? 0)) return false; }
  return true;
}

export const useSession = create<SessionStore>()((set, get) => ({
  phase: 'booting',
  serverOffsetMs: 0,
  worldId: loadWorld(),
  async signIn() {
    set({ phase: 'auth' });
    const issuer = import.meta.env.VITE_AUTH_ISSUER as string | undefined;
    if (issuer) {
      // SpacetimeAuth OIDC, Authorization Code + PKCE (11 §13.3, decision C9). NOT TESTABLE on the
      // local server: this stub only documents the flow. The desktop bridge (`auth.beginLogin`)
      // or a browser redirect to `${issuer}/authorize?...code_challenge` would land on
      // `/auth/callback?code=…`, the renderer exchanges the code with the verifier and hands the
      // id-token to `DbConnectionBuilder.withToken` instead of the anonymous token.
      const { beginPkceLogin } = await import('./auth');
      await beginPkceLogin(issuer);
    }
    set({ phase: 'connecting' });
  },
  signOut() {
    try { localStorage.removeItem(`fk.stdb.token.${import.meta.env.VITE_STDB_DB ?? 'fellowkeep-local'}`); } catch { /* ignore */ }
    set({ phase: 'auth', identity: undefined, token: undefined });
    location.reload();
  },
  serverNow,
  setPhase(phase) { set({ phase }); },
  // a boot that failed and then connected on a retry goes back through the compat gate
  connected(identity, token) { set({ identity, token, error: undefined, phase: get().compat && get().phase !== 'offline' ? get().phase : 'connecting' }); },
  setCompat(row, wasmRuleset, clientVersion) {
    if (!row) { set({ compat: undefined, wasmRuleset, phase: 'ready' }); return; }
    const ok = row.rulesetVersion === wasmRuleset && semverAtLeast(clientVersion, row.minClient);
    set({ compat: row, wasmRuleset, phase: ok ? 'ready' : 'incompatible', error: ok ? undefined : `client ruleset ${wasmRuleset} / v${clientVersion} vs module ruleset ${row.rulesetVersion} / min ${row.minClient}` });
  },
  offline(error) { set({ phase: 'offline', error }); },
  retry() { void import('../net/connection').then((m) => m.reconnectNow()); },
  setWorld(worldId) {
    try { if (worldId === undefined) localStorage.removeItem(WORLD_KEY); else localStorage.setItem(WORLD_KEY, worldId.toString()); } catch { /* ignore */ }
    set({ worldId });
  },
  touchOffset() { set({ serverOffsetMs: serverOffsetMs() }); },
}));
